import { FILTER_GROUPS } from "config/filters";

// Slug -> gateway subPageType + literal searchParams for SDC Near Me Pages.
// SEO copy is supplied by the gateway, not here. breadcrumbLabel drives the breadcrumb text and is the
// H1 fallback when the gateway doesn't return a meta.headline for this subPageType — it no longer has
// to match the H1 verbatim, since meta.headline takes precedence there. See SearchPageV2.js headingTitle.
export const nearMeKeywords = {
	// amenity slugs
	"24-hour-storage-units": {
		subPageType: "24-hour",
		searchParams: { amenity: "24-hour-access" },
		breadcrumbLabel: "24 Hour Storage Units Near Me"
	},
	"climate-controlled-storage-units": {
		subPageType: "climate-controlled",
		searchParams: { amenity: "climate-controlled" },
		breadcrumbLabel: "Climate Controlled Storage Units Near Me"
	},
	"drive-up-storage-units": {
		subPageType: "drive-up",
		searchParams: { amenity: "drive-up-self-storage" },
		breadcrumbLabel: "Drive Up Storage Units Near Me"
	},
	"storage-units-with-free-truck-rental": {
		subPageType: "free-truck-rental",
		searchParams: { amenity: "free-truck-rental" },
		breadcrumbLabel: "Storage Units With Free Truck Rental Near Me"
	},
	// The gateway's amenityMap key for this is "electricity" -> "power".
	"electricity-storage-units": {
		subPageType: "electricity",
		searchParams: { amenity: "electricity" },
		breadcrumbLabel: "Storage Units With Electricity Near Me"
	},
	// Wine has no gateway amenityMap key, so it sends the pre-mapped `amenities` array instead.
	"wine-storage-units": {
		subPageType: "wine",
		searchParams: { amenities: ["onlyWine"] },
		breadcrumbLabel: "Wine Storage Units Near Me"
	},
	// "mini" is a gateway sqft alias, echoed back as-is rather than expanded to "0-25" — keeps it distinct from the 5x5 filter.
	"mini-storage-units": {
		subPageType: "mini",
		searchParams: { sqft: "mini" },
		breadcrumbLabel: "Mini Storage Units Near Me"
	},
	"locker-storage-units": {
		subPageType: "lockers",
		searchParams: { onlyLocker: true },
		breadcrumbLabel: "Locker Storage Units Near Me"
	},
	// vehicleAccommodation must match the filter option's `value`, not the bare `vehicle` kind — it's what checks the vehicleStorage radio on load.
	boat: {
		subPageType: "boat",
		searchParams: {
			type: "vehicle",
			vehicle: "boat",
			vehicleAccommodation: "boat-storage"
		},
		breadcrumbLabel: "Boat Storage Units Near Me"
	},
	car: {
		subPageType: "car",
		searchParams: {
			type: "vehicle",
			vehicle: "car",
			vehicleAccommodation: "car-storage"
		},
		breadcrumbLabel: "Car Storage Units Near Me"
	},
	rv: {
		subPageType: "rv",
		searchParams: {
			type: "vehicle",
			vehicle: "rv",
			vehicleAccommodation: "rv-storage"
		},
		breadcrumbLabel: "RV Storage Units Near Me"
	},
	// size slugs
	"5x5-storage-units": {
		subPageType: "5x5",
		searchParams: { sqft: "0-25" },
		breadcrumbLabel: "5x5 Storage Units Near Me"
	},
	"5x10-storage-units": {
		subPageType: "5x10",
		searchParams: { sqft: "26-60" },
		breadcrumbLabel: "5x10 Storage Units Near Me"
	},
	"5x15-storage-units": {
		subPageType: "5x15",
		searchParams: { sqft: "61-75" },
		breadcrumbLabel: "5x15 Storage Units Near Me"
	},
	"10x10-storage-units": {
		subPageType: "10x10",
		searchParams: { sqft: "76-100" },
		breadcrumbLabel: "10x10 Storage Units Near Me"
	},
	"10x15-storage-units": {
		subPageType: "10x15",
		searchParams: { sqft: "101-150" },
		breadcrumbLabel: "10x15 Storage Units Near Me"
	},
	"10x20-storage-units": {
		subPageType: "10x20",
		searchParams: { sqft: "151-200" },
		breadcrumbLabel: "10x20 Storage Units Near Me"
	},
	"10x25-storage-units": {
		subPageType: "10x25",
		searchParams: { sqft: "201-250" },
		breadcrumbLabel: "10x25 Storage Units Near Me"
	},
	"10x30-storage-units": {
		subPageType: "10x30",
		searchParams: { sqft: "251-300" },
		breadcrumbLabel: "10x30 Storage Units Near Me"
	},
	// deals
	deals: {
		subPageType: "deals",
		searchParams: {},
		breadcrumbLabel: "Storage Unit Deals Near Me"
	}
};

// Maps subPageType → breadcrumbLabel for use in components that receive
// subPageType from Redux (not the URL slug directly).
const subPageTypeMap = Object.values(nearMeKeywords).reduce(
	(acc, { subPageType, breadcrumbLabel, searchParams }) => {
		acc[subPageType] = { breadcrumbLabel, searchParams };
		return acc;
	},
	{}
);

export function getKeywordConfig(slug) {
	return nearMeKeywords[slug] || null;
}

// Returns the static breadcrumb label for an NMP subPageType, or null if unknown.
export function getNMPBreadcrumbLabel(subPageType) {
	return subPageTypeMap[subPageType]?.breadcrumbLabel || null;
}

// Returns the base searchParams that define an NMP (e.g. { amenity: "climate-controlled" }
// or { sqft: "0-25" }) for a given subPageType, or null if unknown.
export function getNMPSearchParams(subPageType) {
	return subPageTypeMap[subPageType]?.searchParams || null;
}

// Amenity CLP slug -> amenities filter option value, so the NMP base can be matched against Redux's checkbox options.
const amenitySlugToFilterValue = {
	"24-hour-access": "twentyFourHourAccess",
	"climate-controlled": "climateControlled",
	"drive-up-self-storage": "driveUp",
	"free-truck-rental": "freeTruckRental",
	electricity: "power"
};

// Returns the NMP's base filter(s) as { name, value } entries, used to detect when the user
// has cleared the page's defining filter. Returns [] for non-NMP pages and the deals NMP.
export function getNMPBaseFilters(subPageType) {
	const params = getNMPSearchParams(subPageType);
	if (!params) return [];

	const baseFilters = [];
	if (params.sqft) {
		baseFilters.push({ name: FILTER_GROUPS.UNIT_SIZE, value: params.sqft });
	}
	if (params.onlyLocker) {
		baseFilters.push({
			name: FILTER_GROUPS.UNIT_SIZE,
			value: "onlyLocker"
		});
	}
	if (params.amenity) {
		const value = amenitySlugToFilterValue[params.amenity];
		if (value) {
			baseFilters.push({ name: FILTER_GROUPS.AMENITIES, value });
		}
	}
	if (params.amenities) {
		params.amenities.forEach((value) => {
			baseFilters.push({ name: FILTER_GROUPS.AMENITIES, value });
		});
	}
	if (params.vehicleAccommodation) {
		baseFilters.push({
			name: FILTER_GROUPS.VEHICLE_STORAGE,
			value: params.vehicleAccommodation
		});
	}
	return baseFilters;
}
